/**
 * Converted Lead Detail page (`/admin/ghl-qualifier/converted/:id`).
 *
 * Renders the timeline of Claude checks (one card per check, labelled with
 * the day it runs on — days 1-8, 15, 22, 30, 60) for a single
 * converted-lead lifecycle. Card variants are chosen based on the lifecycle
 * status + the number of analyses present:
 *  - Confirmed lifecycle: completed cards 1..N, then "Skipped (lifecycle ended)"
 *    cards for any remaining days up to N (per spec, only render up to the day
 *    of confirmation since post-confirmation days are noise).
 *  - Exhausted: one completed card per check (or however many timeline rows exist).
 *  - In Progress: completed cards 1..check_count, pending cards for the rest.
 *  - Scheduled: 0 completed cards, "First check pending" placeholder.
 *  - Cancelled / Failed: header alone, no day cards.
 */

import { useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { ArrowLeft, ExternalLink, RefreshCcw, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { buildGHLContactUrl, truncateId } from '@/lib/ghl-qualifier'
import { convertedCheckDay } from '@/lib/ghl-qualifier-schedule'
import {
  useCancelConvertedLead,
  useConvertedLeadDetail,
  useQualifierGoogleAdsSettings,
  useRetryConvertedLead,
} from '@/hooks/useGHLQualifierDashboard'
import { LifecycleHeader } from '@/components/ghl-qualifier/LifecycleHeader'
import { DailyCheckCard } from '@/components/ghl-qualifier/DailyCheckCard'
import { GoogleAdsUploadDetail } from '@/components/ghl-qualifier/GoogleAdsUploadDetail'

export default function ConvertedLeadDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const detail = useConvertedLeadDetail(id)
  const cancelMutation = useCancelConvertedLead()
  const retryMutation = useRetryConvertedLead()
  // Hoisted to keep hook order stable across renders. Args are passed
  // conditionally — the fetch is gated on the locationId being known.
  const gaSettings = useQualifierGoogleAdsSettings(
    detail.data?.lifecycle.location_id ?? '',
    !!detail.data?.lifecycle.location_id
  )

  const back = () => {
    if (detail.data?.lifecycle.location_id) {
      navigate({
        pathname: `/admin/ghl-qualifier/location/${detail.data.lifecycle.location_id}/converted-leads`,
        search: location.search,
      })
    } else {
      navigate({ pathname: '/admin/ghl-qualifier', search: location.search })
    }
  }

  const cards = useMemo(() => {
    if (!detail.data) return []
    const { lifecycle, timeline } = detail.data
    const max = lifecycle.max_checks
    const out: Array<
      | {
          day: number
          variant: 'completed'
          analysis: (typeof timeline)[number]
          didConfirm: boolean
          confirmationActions?: string[]
        }
      | { day: number; variant: 'skipped'; reason?: string }
      | { day: number; variant: 'pending'; estimatedAt: Date | null }
    > = []

    // Map each timeline analysis row to a check. The worker writes one row
    // per check, ordered by created_at ascending, so the array index + 1 is
    // the check number; `day` is the schedule day that check runs on.
    const completedDays = timeline.length

    if (lifecycle.status === 'completed_converted') {
      // Confirmed lifecycle — render completed cards up to confirm day, then
      // "skipped" cards for the rest. The final completed card carries the
      // confirmation action list.
      for (let i = 0; i < timeline.length; i++) {
        const isLast = i === timeline.length - 1
        const confirmActions: string[] = []
        if (isLast) {
          confirmActions.push('context_converted_leads applied')
          if (lifecycle.monetary_value_written != null) {
            confirmActions.push(
              `Monetary value $${lifecycle.monetary_value_written} written to ${lifecycle.opportunity_id ? truncateId(lifecycle.opportunity_id, 14) : 'opportunity'}`
            )
          }
          // Worker also removes `qualified_no` on confirm; we don't know for
          // sure from the lifecycle row alone, but the worker always issues
          // the remove call as part of the confirmed branch.
          confirmActions.push('qualified_no removed (if previously applied)')
        }
        out.push({
          day: convertedCheckDay(i + 1),
          variant: 'completed',
          analysis: timeline[i],
          didConfirm: isLast,
          confirmationActions: isLast ? confirmActions : undefined,
        })
      }
      // Days after confirmation: render as skipped for context — operators
      // can see "the lifecycle ended on day N" without scrolling further.
      for (let c = completedDays + 1; c <= max; c++) {
        out.push({
          day: convertedCheckDay(c),
          variant: 'skipped',
          reason: `Lifecycle ended on Day ${convertedCheckDay(completedDays)}.`,
        })
      }
      return out
    }

    if (lifecycle.status === 'completed_exhausted') {
      for (let i = 0; i < timeline.length; i++) {
        out.push({
          day: convertedCheckDay(i + 1),
          variant: 'completed',
          analysis: timeline[i],
          didConfirm: false,
        })
      }
      // Pad with skipped entries if the timeline is short of max_checks.
      for (let c = timeline.length + 1; c <= max; c++) {
        out.push({
          day: convertedCheckDay(c),
          variant: 'skipped',
          reason: 'No analysis row recorded.',
        })
      }
      return out
    }

    // `pending` and `processing` are both transient states between daily
    // checks — the drain resets the row to `pending` after each
    // non-confirming check, and `processing` is briefly true while the
    // worker is mid-flight. Both should render the same as `in_progress`
    // from the dashboard's perspective: completed cards for past checks,
    // pending placeholders for the rest.
    if (
      lifecycle.status === 'in_progress' ||
      lifecycle.status === 'scheduled' ||
      lifecycle.status === 'pending' ||
      lifecycle.status === 'processing'
    ) {
      for (let i = 0; i < timeline.length; i++) {
        out.push({
          day: convertedCheckDay(i + 1),
          variant: 'completed',
          analysis: timeline[i],
          didConfirm: false,
        })
      }
      // Pending placeholders. The very next card shows the drain's scheduled
      // time; further-out cards use null since retries can shift them.
      const nextFireAt = lifecycle.next_fire_at ? new Date(lifecycle.next_fire_at) : null
      for (let c = timeline.length + 1; c <= max; c++) {
        out.push({
          day: convertedCheckDay(c),
          variant: 'pending',
          estimatedAt: c === timeline.length + 1 ? nextFireAt : null,
        })
      }
      return out
    }

    // cancelled / failed — no day cards.
    return out
  }, [detail.data])

  if (detail.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-24 w-full" />
        <div className="grid gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    )
  }

  if (!detail.data) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={back} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p className="text-lg font-medium">Lifecycle record not found.</p>
            <p className="text-sm mt-1">
              It may have been deleted or you may not have access.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const { lifecycle, contact } = detail.data
  const directMode =
    gaSettings.data?.conversion_delivery_method === 'google_ads'

  const displayName =
    contact?.full_name ?? contact?.first_name ?? lifecycle.contact_id
  const email = contact?.email ?? null
  const phone = contact?.phone ?? null

  const ghlUrl = buildGHLContactUrl(lifecycle.location_id, lifecycle.contact_id)
  const isCancellable = ['pending', 'processing', 'scheduled', 'in_progress'].includes(
    lifecycle.status
  )
  const isRetryable = lifecycle.status === 'failed'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={back} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to feed
        </Button>
        {isRetryable && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" disabled={retryMutation.isPending}>
                <RefreshCcw className={`h-4 w-4 ${retryMutation.isPending ? 'animate-spin' : ''}`} />
                {retryMutation.isPending ? 'Rerunning…' : 'Rerun check'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Rerun this converted-lead check?</AlertDialogTitle>
                <AlertDialogDescription>
                  The lifecycle resumes at check {(lifecycle.check_count ?? 0) + 1} of{' '}
                  {lifecycle.max_checks} and is processed immediately. A new Claude
                  analysis runs against the current conversation; if a booking was
                  already confirmed and only the tag write failed, the rerun resumes
                  directly at the tag-apply step.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep as failed</AlertDialogCancel>
                <AlertDialogAction
                  onClick={async () => {
                    try {
                      const result = await retryMutation.mutateAsync({ id: lifecycle.id })
                      if (result.immediate) {
                        toast.success('Rerun complete — result updated')
                      } else {
                        toast.success('Check re-queued — it will process within ~2 minutes')
                      }
                    } catch (err) {
                      toast.error(
                        err instanceof Error ? err.message : 'Failed to rerun check'
                      )
                    }
                  }}
                >
                  Rerun check
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {isCancellable && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 text-destructive hover:text-destructive">
                <XCircle className="h-4 w-4" />
                Cancel lifecycle
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel this converted-lead lifecycle?</AlertDialogTitle>
                <AlertDialogDescription>
                  Remaining daily checks will not run. The contact will not be evaluated for
                  conversion, the `context_converted_leads` tag will not be applied, and no
                  monetary value will be written to the opportunity. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep lifecycle</AlertDialogCancel>
                <AlertDialogAction
                  onClick={async () => {
                    try {
                      await cancelMutation.mutateAsync({ id: lifecycle.id })
                      toast.success('Converted-lead lifecycle cancelled')
                    } catch (err) {
                      toast.error(
                        err instanceof Error ? err.message : 'Failed to cancel lifecycle'
                      )
                    }
                  }}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Cancel lifecycle
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div>
        <h1 className="text-2xl font-bold">{displayName}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {[email, phone, `Contact ${lifecycle.contact_id}`]
            .filter(Boolean)
            .join('  ·  ')}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Started {new Date(lifecycle.created_at).toLocaleString()} · Location:{' '}
          <span className="font-mono">{lifecycle.location_id}</span>
        </p>
      </div>

      <LifecycleHeader lifecycle={lifecycle} />

      {cards.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground text-sm">
            — No checks ran. —
          </CardContent>
        </Card>
      ) : (
        <div>
          <h2 className="text-lg font-semibold mb-3">Daily check timeline</h2>
          <div className="space-y-3">
            {cards.map((card) => (
              <DailyCheckCard
                key={`${card.variant}-${card.day}`}
                {...(card as Parameters<typeof DailyCheckCard>[0])}
              />
            ))}
          </div>
        </div>
      )}

      <GoogleAdsUploadDetail
        contactId={lifecycle.contact_id}
        eventType="converted"
        enabled={directMode}
      />

      <div className="flex flex-wrap gap-2 pt-2">
        <Button asChild variant="outline" size="sm" className="gap-2">
          <a href={ghlUrl} target="_blank" rel="noopener noreferrer">
            View in GHL
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </Button>
      </div>
    </div>
  )
}
